import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { useAuthcontext } from "../Contexts/AuthContext";
import { useAppContext } from "../Contexts/AppContext";
import toast from "react-hot-toast";

const Home = () => {
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { TheatorUser } = useAuthcontext();
  const { room, setVideoId } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrendingVideos = async () => {
      try {
        setLoading(true);
        // Using the API key from the project
        const API_KEY = "AIzaSyDptw29QDvr3hBZxpnMOTUO-RPPQiGL0NQ";
        const response = await axios.get(
          `https://youtube.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&chart=mostPopular&regionCode=IN&maxResults=6&videoCategoryId=10&key=${API_KEY}`
        );
        setTrendingVideos(response.data.items);
      } catch (error) {
        console.error("Error fetching trending videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingVideos();
  }, []);

  const playHandle = (vId) => {
    if (!TheatorUser) {
      navigate("/login");
      toast.error("Please Login First");
      return;
    }
    
    if (room) {
      setVideoId(vId);
      navigate(`/video/${vId}/${room}`);
      toast.success("Joined Theater!");
    } else {
      toast.error("Please Join a Room first from the Theater page");
      navigate("/theator");
    }
  };

  return (
    <div className="bg-[#0f172a] min-h-screen text-slate-100 selection:bg-blue-500/30 overflow-x-hidden">
      <Helmet>
        <title>SyncMovie - Watch Movies Together Online</title>
        <meta name="description" content="Watch movies together with friends online in real-time." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-[20%] right-[-5%] w-[35%] h-[35%] bg-purple-600/20 blur-[120px] rounded-full"></div>
        </div>

        <div className="container mx-auto px-6 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-extrabold mb-10 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-blue-400 leading-[1.15] py-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Watch Together, <br className="hidden md:block" /> Anytime, Anywhere
          </motion.h1>
          
          <motion.p 
            className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Experience movies and TV shows in perfect synchronization with your favorite people. 
            Real-time chat, emoji reactions, and seamless playback control.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <Link to="/theator">
              <button className="group relative px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 hover:-translate-y-1 active:translate-y-0">
                <span className="flex items-center gap-2 text-lg">
                  Launch Your Theater
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Featured Section */}
      <div className="container mx-auto px-6 pb-24">
        <div className="flex items-center justify-between mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-2">Trending on YouTube</h2>
            <p className="text-slate-400">Most watched music and entertainment today</p>
          </motion.div>
          
          <div className="h-px flex-1 bg-gradient-to-r from-slate-800 to-transparent mx-8 hidden md:block"></div>
          
          <div className="text-sm font-mono text-blue-400 bg-blue-500/10 px-4 py-2 rounded-full flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            LIVE UPDATES
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[16/10] bg-slate-800/50 rounded-3xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {trendingVideos.map((video, index) => (
              <motion.div
                key={video.id}
                className="group relative bg-slate-800/50 rounded-3xl overflow-hidden border border-slate-700/50 hover:border-blue-500/50 transition-all duration-500 cursor-pointer"
                onClick={() => playHandle(video.id)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="aspect-[16/10] w-full bg-slate-900 overflow-hidden">
                  <div 
                    className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url(${video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high.url})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/10 to-transparent opacity-80"></div>
                </div>
                
                <div className="absolute top-4 left-4">
                  <span className="bg-red-600/90 backdrop-blur-md text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg flex items-center gap-1.5 shadow-lg">
                    Trending
                  </span>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 group-hover:text-blue-400 transition-colors line-clamp-1">
                    {video.snippet.title}
                  </h3>
                  <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                    <span className="text-slate-300 font-semibold">{video.snippet.channelTitle}</span>
                    <span>•</span>
                    <span>{(parseInt(video.statistics.viewCount) / 1000000).toFixed(1)}M Views</span>
                  </div>
                </div>

                {/* Play Overlay */}
                <div className="absolute inset-x-6 bottom-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <button className="w-full py-3 bg-white text-slate-950 font-bold rounded-xl hover:bg-blue-50 transition-colors shadow-2xl flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    Watch Sync
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
